/**
 * Route-level error boundary: a render crash becomes an explicit FAILED
 * state with a retry, never a blank screen — and it is recorded as a beta
 * friction signal the user can attach to a report.
 */
import React from "react";
import { Button } from "../ui/button";
import { AlertTriangle, RefreshCw } from "lucide-react";
import { recordBetaSignal } from "../../lib/betaTelemetry";

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidUpdate(prevProps) {
    // Navigating to a different route clears a previous crash — one broken
    // screen must not brick every screen after it.
    if (this.state.error && prevProps.resetKey !== this.props.resetKey) {
      this.setState({ error: null });
    }
  }

  componentDidCatch(error, info) {
    recordBetaSignal("render-crash", {
      message: String(error?.message || error),
      component: info?.componentStack?.split("\n")[1]?.trim(),
    });
  }

  render() {
    if (this.state.error) {
      return (
        <div className="flex flex-col items-center gap-3 py-16 text-center">
          <AlertTriangle className="w-10 h-10 text-red-400/70" />
          <p className="text-base font-medium">This screen failed to render</p>
          <p className="text-sm text-muted-foreground max-w-md break-words">
            {String(this.state.error?.message || this.state.error)}
          </p>
          <Button variant="outline" onClick={() => this.setState({ error: null })}>
            <RefreshCw className="w-4 h-4 mr-1.5" />
            Try again
          </Button>
        </div>
      );
    }
    return this.props.children;
  }
}

export default ErrorBoundary;
